//  src/utils/perfContext.ts

export type PerfMeta = Record<string, string | number | boolean | null | undefined>;

type PerfContext = {
  label: string;
  ts: number; // performance.now() ms
};

function perfNow(): number {
  return typeof performance?.now === 'function' ? performance.now() : Date.now();
}

function perfMark(name: string): void {
  if (typeof performance?.mark === 'function') {
    performance.mark(name);
  }
}

function perfMeasure(name: string, startMark: string, endMark: string): void {
  if (typeof performance?.measure === 'function') {
    performance.measure(name, startMark, endMark);
  }
}

//  Default baseline so context is never undefined.
const BASE_BOOT: PerfContext = { label: 'App boot', ts: perfNow() };

let current: PerfContext = BASE_BOOT;

export function setPerfContext(label: string): void {
  current = { label, ts: perfNow() };
}

//  "clear" goes back to baseline
export function clearPerfContext(): void {
  current = BASE_BOOT;
}

export function getPerfContext(): { label: string; ageMs: number } {
  return { label: current.label, ageMs: perfNow() - current.ts };
}

/**
 * Wapper for "do X while context is Y".
 * Clears context if it hasn't been overwritten.
 */
export async function withPerfContext<T>(label: string, fn: () => Promise<T>): Promise<T> {
  const prev = current;
  setPerfContext(label);

  try {
    return await fn();
  }
  finally {
    //  Restore previous if nothing else changed the context during the operation
    if (current.label === label) {
      current = prev;
    }
  }
}

type LogRocketLike = {
  track?: (eventName: string, props?: Record<string, unknown>) => void;
};

function getLogRocket(): LogRocketLike | undefined {
  return (window as unknown as { LogRocket?: LogRocketLike }).LogRocket;
}
function track(eventName: string, props: PerfMeta): void {
  const lr = getLogRocket();
  if (lr && typeof lr.track === 'function') {
    lr.track(eventName, props as Record<string, unknown>);
  }
}

//  Current Context's age to LogRocket
export function reportCurrentContext(extra: PerfMeta = {}, eventName = 'ui_perf'): void {
  const { label, ageMs } = getPerfContext();
  track(eventName, { label, durationMs: Math.round(ageMs), ...extra });
}

/**
 * Times an async span and reports only if it exceeds slowMS or always=true.
 * Call it only around actions we care about.
 */
export async function withPerfSpan<T>(
  label: string,
  fn: () => Promise<T>,
  meta: PerfMeta = {},
  opts: { slowMs?: number; always?: boolean; eventName?: string; mark?: boolean } = {},
): Promise<T> {
  const slowMs = opts.slowMs != null ? opts.slowMs : 500;
  const { eventName } = opts;
  const mark = opts.mark != null ? opts.mark : true;

  const start = perfNow();
  const startMark = mark ? `span:${label}:start` : '';
  const endMark = mark ? `span:${label}:end` : '';
  const measureName = mark ? `span:${label}` : '';

  let ok = true;

  if (mark) perfMark(startMark);

  try {
    return await withPerfContext(label, fn);
  }
  catch (e) {
    ok = false;
    throw e;
  }
  finally {
    if (mark) {
      perfMark(endMark);
      try {
        perfMeasure(measureName, startMark, endMark);
      }
      catch {
        //  ignore if marks get cleared elsewhere
      }
    }

    const durationMs = perfNow() - start;

    if ((opts.always || durationMs >= slowMs) && eventName) {
      track(eventName, {
        label,
        durationMs: Math.round(durationMs),
        ok,
        ...meta,
      });
    }
  }
}

/**
 * Wait until predicate becomes true (useful when not using correlation keys).
 * Rejects on timeout to avoid silent hangs
 */
export function waitUntil(
  predicate: () => boolean,
  opts: { timeoutMs?: number; intervalMs?: number } = {},
): Promise<void> {
  const timeoutMs = opts.timeoutMs != null ? opts.timeoutMs : 15_000;
  const intervalMs = opts.intervalMs != null ? opts.intervalMs : 50;

  if (predicate()) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const start = perfNow();
    const timer = window.setInterval(() => {
      if (predicate()) {
        window.clearInterval(timer);
        resolve();
        return;
      }
      if (perfNow() - start >= timeoutMs) {
        window.clearInterval(timer);
        reject(new Error('waitUntil timeout'));
      }
    }, intervalMs);
  });
}
